/**
 * Returns the JWT to send with KG API calls.
 *
 * The main LeanByte app supports two auth flows:
 *  - LDAP login → JWT in `localStorage.authToken`
 *  - Microsoft (Azure AD) login → token in MSAL's sessionStorage cache.
 *    MSAL stores access tokens as JSON with shape
 *    { credentialType: "AccessToken", secret: "<jwt>", target: "<scopes>" }.
 *    We pick the token whose `target` (scope string) includes the LeanByte
 *    app registration's client ID, since that's the only access token whose
 *    `aud` claim matches what FastAPI's validator expects.
 *
 * Client ID is sourced from `window.leanbyte.oAuthConfig.clientId` — the same
 * global the backend injects at page render and that `msal-config.ts` and
 * `AppConfigService` already read. Never hard-code it.
 */
function getLeanbyteClientId(): string {
  return (window as any)?.['leanbyte']?.oAuthConfig?.clientId ?? '';
}

/**
 * Wipe every FAB / KG-chat localStorage entry on this machine. Called on
 * intentional logout (LDAP and MSAL paths) so the next user signing in on
 * the same browser can't inspect the previous user's persisted conversation.
 *
 * Safe to call when nothing is stored. Does NOT touch session/auth tokens or
 * any non-FAB key. Do NOT call this on token-expiry — same user re-logging in
 * should see their conversation intact.
 */
export function clearKgChatStorage(): void {
  try {
    const doomed: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && (k.startsWith('kg-fab-chat:') || k.startsWith('kg-fab:'))) {
        doomed.push(k);
      }
    }
    for (const k of doomed) localStorage.removeItem(k);
  } catch {
    // privacy mode / quota — best effort
  }
}

/**
 * Stable identifier for the signed-in user (used to scope localStorage keys).
 * Prefers Azure AD `oid`, falls back to JWT `sub`, then 'anon' if no token.
 */
export function getKgUserId(): string {
  const token = getAuthToken();
  if (!token) return 'anon';
  try {
    const payload = JSON.parse(
      atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')),
    );
    return payload.oid || payload.sub || 'anon';
  } catch {
    return 'anon';
  }
}

export function getAuthToken(): string | null {
  const direct = localStorage.getItem('authToken');
  if (direct) {
    return direct;
  }
  const clientId = getLeanbyteClientId();
  if (!clientId) {
    // No MSAL config available — only LDAP tokens are reachable from here.
    return null;
  }
  for (const key of Object.keys(sessionStorage)) {
    const raw = sessionStorage.getItem(key);
    if (!raw) {
      continue;
    }
    try {
      const obj = JSON.parse(raw);
      if (
        obj?.credentialType === 'AccessToken' &&
        typeof obj.target === 'string' &&
        obj.target.includes(clientId)
      ) {
        return obj.secret as string;
      }
    } catch {
      // not a JSON entry — skip
    }
  }
  return null;
}
